import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { X } from 'lucide-react';
import { standaloneFieldSx } from '../common/fieldStyles';
import { messageFromApiErrorBody } from '../common/apiErrorMessage';
import { httpClient } from '../common/httpclient';

const formatCellphone = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 11);
    let formatted = '';
    if (digits.length > 0) {
        formatted = `(${digits.slice(0, 2)}`;
        if (digits.length > 2) {
            formatted += `) ${digits.slice(2, 7)}`;
            if (digits.length > 7) {
                formatted += `-${digits.slice(7, 11)}`;
            }
        }
    }
    return formatted;
};

/**
 * Cadastro rápido de cliente em drawer (PDV): tipo (PF/PJ), Nome/Razão Social e Celular.
 * Usa os endpoints existentes `/customers/person` e `/customers/enterprise`, que não exigem
 * CPF/CNPJ. Ao salvar chama `onSuccess` com `{ id, ID, Name, Cellphone }` (formato da listagem
 * de clientes) para o chamador selecionar o cliente criado.
 */
export default function QuickCustomerDrawer({ open, onClose, onSuccess }) {
    const [customerType, setCustomerType] = useState('pf');
    const [name, setName] = useState('');
    const [cellphone, setCellphone] = useState('');
    const [errorName, setErrorName] = useState('');
    const [errorCellphone, setErrorCellphone] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setCustomerType('pf');
            setName('');
            setCellphone('');
            setErrorName('');
            setErrorCellphone('');
            setSubmitError('');
            setSaving(false);
        }
    }, [open]);

    const isPf = customerType === 'pf';

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (saving) return;

        const trimmedName = name.trim().replace(/\s+/g, ' ');
        let valid = true;
        if (!trimmedName) {
            setErrorName('Campo obrigatório');
            valid = false;
        }
        if (cellphone && cellphone.replace(/\D/g, '').length < 10) {
            setErrorCellphone('Celular incompleto');
            valid = false;
        }
        if (!valid) return;

        setSaving(true);
        setSubmitError('');
        try {
            let response;
            if (isPf) {
                const [firstName, ...rest] = trimmedName.split(' ');
                response = await httpClient.post('/customers/person', {
                    firstName: firstName.slice(0, 50),
                    lastName: rest.join(' ').slice(0, 50),
                    cellPhone: cellphone
                });
            } else {
                response = await httpClient.post('/customers/enterprise', {
                    legalName: trimmedName,
                    tradeName: trimmedName,
                    cellPhone: cellphone
                });
            }
            const res = response?.data || {};
            const customerId = res.id || res.ID || res.Id;
            if (!customerId) {
                setSubmitError('Cliente cadastrado, mas não foi possível selecioná-lo. Busque-o na lista.');
                setSaving(false);
                return;
            }
            onSuccess({
                id: customerId,
                ID: customerId,
                Name: trimmedName,
                Cellphone: cellphone
            });
            onClose();
        } catch (error) {
            setSubmitError(
                messageFromApiErrorBody(error?.response?.data) ||
                (typeof error?.response?.data === 'string' && error.response.data) ||
                'Não foi possível cadastrar o cliente.'
            );
            setSaving(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={saving ? undefined : onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, maxWidth: { xs: '100vw', sm: '92vw' }, display: 'flex', flexDirection: 'column' } }}
        >
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: (t) => `1px solid ${t.palette.divider}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>Novo cliente</Typography>
                        <Typography variant="body2" color="text.secondary">Cadastro rápido — o cliente será selecionado na venda</Typography>
                    </Box>
                    <IconButton onClick={onClose} disabled={saving} size="small" sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '10px' }}>
                        <X size={16} />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        value={customerType}
                        onChange={(e, value) => {
                            if (!value) return;
                            setCustomerType(value);
                            setErrorName('');
                        }}
                        sx={{ width: '100%', '& .MuiToggleButton-root': { flex: 1, height: '52px', textTransform: 'none', fontWeight: 700, fontSize: '13.5px', borderWidth: '1.5px' } }}
                    >
                        <ToggleButton value="pf">Pessoa Física</ToggleButton>
                        <ToggleButton value="pj">Pessoa Jurídica</ToggleButton>
                    </ToggleButtonGroup>

                    <TextField
                        autoFocus
                        label={isPf ? 'Nome' : 'Razão Social'}
                        fullWidth
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setErrorName('');
                        }}
                        error={Boolean(errorName)}
                        helperText={errorName}
                        inputProps={{ maxLength: isPf ? 100 : 255 }}
                        sx={standaloneFieldSx}
                    />

                    <TextField
                        label="Celular"
                        type="tel"
                        fullWidth
                        value={cellphone}
                        onChange={(e) => {
                            setCellphone(formatCellphone(e.target.value));
                            setErrorCellphone('');
                        }}
                        error={Boolean(errorCellphone)}
                        helperText={errorCellphone}
                        placeholder="(19) 99999-9999"
                        inputProps={{ maxLength: 15 }}
                        sx={standaloneFieldSx}
                    />

                    {submitError && (
                        <Typography variant="body2" color="error">{submitError}</Typography>
                    )}
                </Box>

                <Box sx={{ px: 3, py: 2, borderTop: (t) => `1px solid ${t.palette.divider}`, display: 'flex', gap: 1.5 }}>
                    <Button variant="outlined" onClick={onClose} disabled={saving} fullWidth>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={saving} fullWidth>
                        {saving ? 'Salvando...' : 'Salvar e selecionar'}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
}
